import { useState, useRef, useEffect } from "react";
import { IconButton, Tooltip, TextField, InputAdornment, Stack, Typography, Button, Box, Paper } from "@mui/material";
import { isEmpty } from "lodash";
import { useSelector, useDispatch } from "react-redux";
import { styled } from "@mui/material/styles";
import { UPDATE_NONCONVERSATIONAL_FILTERS } from "../../../state/constants";

import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import CommonModalPopUp from "../../ui/Modals/ModalPopupCommon";
import CircularProgress from '@mui/material/CircularProgress';

import { setBotWaitLoaderWithShimmer, setButtonLoader } from "../../../state/actions/loader";
import { showToast } from "../../ui/toaster";
import { isUserOnline, saveInLocalStorage } from "../../../services/storage/LocalStorage";

const LookupDropCustom = styled("li")(() => ({
	display: "flex",
	width: "100%",
	flexDirection: "row",
	alignItems: "center",
	justifyContent: "space-between !important",
	padding: "8px 20px",
	cursor: "pointer",
	"&:hover": {
		backgroundColor: "#eeeeee",
	},
}));

const MainButton = styled(Button)(() => ({
	border: 'none',
	padding: '7px 45px',
	borderRadius: 20,
	textTransform: 'inherit',
	'&:hover': { border: 'none' },
}));

export const CancelButton = styled(MainButton)(() => ({
	backgroundColor: '#e0e8ff',
	color: '#638dff',
	border: 'none',
	fontFamily: 'SF Pro Text Bold',
	fontSize: 14,
	boxShadow: 'none',
	'&:hover': { backgroundColor: '#eef2ff' },
	'&:focus': {
		boxShadow: '0 0 6px 0 rgba(99, 141, 255, 0.6)',
		backgroundColor: '#eef2ff',
	},
	'&:disabled': {
		backgroundColor: '#e8ecf8',
		color: '#aeb8d6',
		border: 'none',
	},
}));

export const ConfirmButton = styled(MainButton)(() => ({
	backgroundColor: '#638dff',
	color: '#FFFFFF',
	fontFamily: 'SF Pro Text Bold',
	fontSize: 14,
	width: '141px',
	height: 38,
	boxShadow: 'none',
	'&:hover': { backgroundColor: '#4c71d6' },
	'&:focus': { boxShadow: '0 0 6px 0 #638dff !important' },
	'&:disabled': {
		backgroundColor: '#aeb8d6',
		color: '#e8ecf8',
	},
}));

const filter = createFilterOptions();

const FMFilterComponent = ({
	options,
	handleAction,
	updateReduxFlags,
	setActiveFilterName,
	addNewFilter,
	handleClearFilterAction
}) => {

	const [filters, setFilters] = useState([]);
	const [filterName, setFilterName] = useState({});
	const [showFilterDeleteConfirmationModal, setFilterDeleteConfimationModal] = useState(false);
	const [saveDeleteFilterOption, setSaveDeleteFilterOptions] = useState(null);

	const NonConversationalFilter = useSelector((state) => state.filters);
	const buttonLoader = useSelector((state) => state.loader.buttonLoader);
	const searchLoader = useSelector((state) => state.loader.searchLoader);

	const dispatch = useDispatch();
	const isOnline = isUserOnline();
	const autoCompleteRef = useRef();
	const clickedActions = useRef(false);

	useEffect(() => {
		if (options?.availableFilters) {
			const updatedFilters = options?.availableFilters?.map((item) => {
				return { ...item, title: item.name, value: item.name };
			}) || []
			setFilters(updatedFilters);
		}
	}, [options?.availableFilters]);


	useEffect(() => {
		if (buttonLoader === false && showFilterDeleteConfirmationModal && saveDeleteFilterOption) {
			showToast('success', 'Filter deleted successfully.');
			setFilterDeleteConfimationModal(false);
			setSaveDeleteFilterOptions(null);
		}
	}, [buttonLoader]);


	useEffect(() => {
		if (clickedActions.current) {
			setTimeout(() => {
				clickedActions.current = false;
			}, 500);
		}
	}, [clickedActions.current]);


	useEffect(() => {
		if (!options?.activeFilterName) {
			setFilterName(null);
			return;
		}

		const name = NonConversationalFilter?.clearActiveFilterName
			? null
			: options.activeFilterName;

		setFilterName(name ? { value: name, title: name } : null);
	}, [options?.activeFilterName, NonConversationalFilter?.clearActiveFilterName]);


	const handleOnClickFilter = () => {
		if (filters?.length) {
			updateReduxFlags({
				openDrawer: false,
				showFilterSearchInput: true,
			});
		} else {
			updateReduxFlags({
				openDrawer: false,
				showFilterSearchInput: true,
				isNewFilter: true,
			});
		}
	};


	const handleActionClick = (option, action) => {
		dispatch(setButtonLoader(true));
		dispatch(setBotWaitLoaderWithShimmer(true));
		clickedActions.current = true;
		handleAction(action, option.title, null, "deleteAction");
	};


	const handleClearFilter = (e) => {
		handleClearFilterAction()
		updateReduxFlags({
			openDrawer: false,
			showFilterSearchInput: false,
			activeFilterName: ""
		});
		dispatch({ type: UPDATE_NONCONVERSATIONAL_FILTERS, data: { clearActiveFilterName: false } });
		e?.stopPropagation();
	};

	// Custom Paper component to add buttons at the bottom
	const CustomPaper = (props) => {
		const { children, ...other } = props;

		const handleCancel = (e) => {
			e.stopPropagation();
			if (document.activeElement) document.activeElement.blur();
		};

		const handleAddFilter = (e) => {
			e.stopPropagation();
			saveInLocalStorage("previousActiveNonConversationFilter", options.activeFilterName);
			setActiveFilterName('');
			updateReduxFlags({
				openDrawer: true,
				showFilterSearchInput: true,
				isNewFilter: true,
			});
			addNewFilter();
		};

		return (
			<Paper {...props} className="bg-white !rounded-10 !shadow-shd-on !py-0">
				{props.children}
				<Box sx={{ p: 1, display: 'flex', gap: 1, justifyContent: 'space-between' }}>
					<Button
						className="!text-size-14 !rounded-10 !border !border-gray-300 !text-gray-300"
						variant="outlined"
						size="small"
						onMouseDown={(e) => e.preventDefault()}
						onClick={handleCancel}
						sx={{ flex: 1 }}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						size="small"
						className="!bg-blue !text-size-14 !rounded-10"
						onMouseDown={(e) => e.preventDefault()}
						onClick={handleAddFilter}
						sx={{ flex: 1 }}
					>
						Add Filter
					</Button>
				</Box>
			</Paper>
		);
	};


	return (
		<>
			<Tooltip title="Filter">
				{options?.showFilterSearchInput || options?.activeFilterName ? (
					<Autocomplete
						className="filter-autocomplete"
						ref={autoCompleteRef}
						sx={{
							minWidth: 260,
							backgroundColor: "#fff",
							"& .MuiOutlinedInput-root": {
								"& fieldset": {
									borderColor: "#ccc", 
								},
								"&:hover fieldset": {
									borderColor: "#92afff", 
								},
								"&.Mui-focused fieldset": {
									border: "1px solid #92afff", 
								},
							},
							"& input[type=text]": { height: 3 },
						}}
						fullWidth
						value={filterName?.value || null}
						onChange={(event, newValue) => {
							if (!clickedActions.current) {
								dispatch({ type: UPDATE_NONCONVERSATIONAL_FILTERS, data: { clearActiveFilterName: false } });
								if (typeof newValue === "string") {
									setFilterName({
										title: newValue,
										value: newValue,
									});
									handleAction("filter", newValue.inputValue);
								} else if (newValue && newValue.title) {
									// Create a new value from the user input
									setFilterName({
										title: newValue.value,
										value: newValue.value,
									});
									handleAction("filter", newValue.inputValue || newValue.value);
								}
							} else {
								console.log("HERr");
							}
						}}
						filterOptions={(options, params) => {
							console.log("filterOptions", options);

							// Filter out corrupted data
							const filteredOptions = options.filter((option) => typeof option.name === "string");
							const filteredIncorrectOptions = options.filter((option) => typeof option.name !== "string");

							if (!isEmpty(filteredIncorrectOptions)) {
								console.error("Incorrect Filter Options: ", filteredIncorrectOptions);
								console.table(filteredIncorrectOptions);
							}

							const filtered = filter(filteredOptions, params);

							// Add "No Filter Found" option if nothing matched
							if (filtered.length === 0 && params.inputValue !== "") {
								filtered.push({
									title: `No Filter Found for "${params.inputValue}"`,
									value: params.inputValue,
									inputValue: params.inputValue,
									readOnly: true, // prevent delete/edit buttons
								});
							}

							return filtered;
						}}
						selectOnFocus
						autoHighlight
						PaperComponent={CustomPaper}
						clearOnBlur
						handleHomeEndKeys
						id="free-solo-with-text-demo"
						options={filters}
						freeSolo
						getOptionLabel={(option) => {
							if (typeof option === "string") {
								return option;
							}
							if (option.inputValue) {
								return option.inputValue;
							}
							return option.title;
						}}
						groupBy={(option) => option.set}
						renderOption={(props, option) =>
							option?.title?.startsWith("Add ") ? (
								<LookupDropCustom {...props}>
									<span>{option?.title}</span>
								</LookupDropCustom>
							) : (
								<LookupDropCustom {...props} className="under-list-item">
									<span>{option?.title}</span>
									{!option?.readOnly && (
										<div>
											<Tooltip
												onClick={(e) => {
													dispatch({ type: UPDATE_NONCONVERSATIONAL_FILTERS, data: { clearActiveFilterName: false } });
													handleActionClick(option, "editFilter");
													e.stopPropagation();
												}}
												title="Edit"
											>
												<EditOutlinedIcon
													sx={{
														color: "#44485a",
														opacity: 0.7,
														marginRight: "10px",
													}}
												/>
											</Tooltip>
											<Tooltip
												onClick={() => (setSaveDeleteFilterOptions(option), setFilterDeleteConfimationModal(true))}
												title="Delete"
											>
												<DeleteOutlineOutlinedIcon
													sx={{
														color: "#44485a",
														opacity: 0.7,
													}}
												/>
											</Tooltip>
										</div>
									)}
								</LookupDropCustom>
							)
						}
						renderInput={(params) => {
							return (
								<TextField
									{...params}
									placeholder="Filter Name"
									variant="outlined"
									fullWidth
									sx={{
										"& > *:first-of-type": { paddingRight: "9px !important" },
										"& .MuiOutlinedInput-root": {
											// pr: 1, // replaces paddingRight: 9px !important
										},
									}}
									InputProps={{
										...params.InputProps,
										endAdornment: searchLoader ? (
											<CircularProgress size={16} sx={{ ml: 3 }} />
										) : (
											<InputAdornment position="end">
												<ClearIcon
													sx={{
														color: "text.secondary",
														cursor: "pointer",
													}}
													onClick={handleClearFilter}
												/>
											</InputAdornment>
										),
									}}
								/>

							);
						}}
					/>
				) : (
					<IconButton
						sx={{
							display: "inline-flex",
							justifyContent: "center",
							width: "40px",
							height: "40px",
							padding: "8px",
							objectFit: "contain",
							borderRadius: "6px",
						}}
						aria-label="FilterList"
						disabled={!isOnline}
						onClick={() => handleOnClickFilter()}
						size="large"
					>
						<FilterListIcon color={"#98B0C8"} />
					</IconButton>
				)}
			</Tooltip>
			{
				showFilterDeleteConfirmationModal &&
				<CommonModalPopUp
					onClose={() => {
						setSaveDeleteFilterOptions("");
						setFilterDeleteConfimationModal(false);
					}}
					noHeader
				>
					<Stack justifyContent="center" alignItems="center" sx={{ p: 2 }}>
						<Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
							Delete Filter
						</Typography>

						<Stack justifyContent="center" alignItems="center" sx={{ mb: 3 }}>
							<Typography textAlign="center" sx={{ color: "text.secondary" }}>
								This operation will delete this filter preset permanently.
								Are you sure you want to proceed?
							</Typography>
						</Stack>

						<Stack
							direction="row"
							justifyContent="space-between"
							alignItems="center"
							gap={2}
						>
							<CancelButton
								color="primary"
								variant="outlined"
								onClick={() => setFilterDeleteConfimationModal(false)}
							>
								Cancel
							</CancelButton>

							<ConfirmButton
								variant="contained"
								color="primary"
								onClick={() => handleActionClick(saveDeleteFilterOption, "filterDelete")}
								disabled={buttonLoader}
							>
								{buttonLoader ? (
									<CircularProgress size={14} sx={{ color: "#fff" }} />
								) : (
									"Confirm"
								)}
							</ConfirmButton>
						</Stack>
					</Stack>
				</CommonModalPopUp>
			}
		</>
	);
};

export default FMFilterComponent;
